import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const lines: { q: string; a: string }[] = [
  { q: 'Who instructed the AI?', a: 'operator identity recorded' },
  { q: 'What was the intended outcome?', a: 'intent captured before run' },
  { q: 'What did it change?', a: 'diff logged to audit trail' },
  { q: 'What evidence proves it worked?', a: 'sources and checks attached' },
  { q: 'What risks were accepted?', a: 'scope and limits declared' },
  { q: 'Who is accountable?', a: 'human sign-off required' },
];

/**
 * A terminal that types each governance question, "resolves" it, and prints an
 * answer — an evidence/audit texture for the "why now" story. Loops.
 */
export function AuditTerminal() {
  const [count, setCount] = useState(0); // resolved lines
  const [typed, setTyped] = useState(''); // currently typing question

  useEffect(() => {
    const t: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const run = (idx: number) => {
      if (cancelled) return;
      if (idx >= lines.length) {
        t.push(setTimeout(() => {
          setCount(0);
          setTyped('');
          run(0);
        }, 2600));
        return;
      }
      const q = lines[idx].q;
      let i = 0;
      const typeNext = () => {
        if (cancelled) return;
        i++;
        setTyped(q.slice(0, i));
        if (i < q.length) {
          t.push(setTimeout(typeNext, 26));
        } else {
          t.push(setTimeout(() => {
            setCount(idx + 1);
            setTyped('');
            run(idx + 1);
          }, 480));
        }
      };
      typeNext();
    };

    t.push(setTimeout(() => run(0), 500));
    return () => {
      cancelled = true;
      t.forEach(clearTimeout);
    };
  }, []);

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-950 font-mono shadow-[0_30px_80px_-40px_rgba(7,78,220,0.4)] backdrop-blur-xl">
      <div className="flex items-center gap-2 border-b border-white/10 bg-slate-900/80 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-2 text-white/50">governance.audit</span>
      </div>
      <div
        className="space-y-1.5 overflow-hidden px-4 py-4 flex flex-col-reverse"
        style={{ height: '232px', flexShrink: 0 }}
      >
        {lines.slice(0, count).reverse().map((l) => (
          <motion.div
            key={l.q}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            className="leading-relaxed"
          >
            <span className="text-[#074EDC]">$ </span>
            <span className="text-white/85">{l.q}</span>
            <span className="ml-2 inline-flex items-center gap-1" style={{ color: '#4AAC00' }}>
              <Check className="h-3.5 w-3.5" />
              {l.a}
            </span>
          </motion.div>
        ))}
        {count < lines.length && (
          <div className="leading-relaxed">
            <span className="text-[#074EDC]">$ </span>
            <span className="text-white/85">{typed}</span>
            <motion.span
              aria-hidden
              className="ml-0.5 inline-block h-4 w-2 -translate-y-0.5 bg-[#074EDC] align-middle"
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.9, repeat: Infinity }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
